/**
 * SignatureCapture Component
 *
 * Full signature capture form with signer information,
 * digital signature canvas, and GPS capture.
 *
 * @module pages/technician/signature/SignatureCapture
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaPen,
  FaEraser,
  FaCheck,
  FaMapMarkerAlt,
  FaSpinner,
  FaTimes,
  FaExclamationCircle,
  FaUser,
  FaBriefcase,
  FaIdCard,
} from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { captureSignature } from '../../../services/signatureService';
import {
  SIGNATURE_TYPE,
  SIGNATURE_TYPE_LABELS,
  canCaptureSignatureType,
  signatureDenialMessage,
} from '../../../utils/constants';
import SignatureCanvas from './SignatureCanvas';
import './SignatureCapture.css';

const SignatureCapture = () => {
  const { projectId, taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const canvasRef = useRef(null);

  // Get signature type from URL or default.
  // SIGNATURE_TYPE.CLIENT_APPROVAL never existed, so this defaulted to
  // `undefined` and the submit died on "Se requiere el tipo de firma" (#311).
  const signatureType = searchParams.get('type') || SIGNATURE_TYPE.CLIENT_ACCEPTANCE;

  // `type` viene de la URL, así que la lista recortada de SignatureList no
  // alcanza: se llega acá igual escribiendo el query param. Se comprueba antes
  // de que el firmante dibuje nada — hacer firmar a alguien y rebotarlo después
  // es peor que no ofrecerlo (#525).
  const typeAllowed = canCaptureSignatureType(profile?.role, signatureType);

  // Form state
  // signer_id_number matches the live column name (and signatureService's
  // documented input), so the value no longer has to be re-mapped downstream.
  const [formData, setFormData] = useState({
    signer_name: '',
    signer_role: '',
    signer_id_number: '',
    notes: '',
  });

  // UI state
  const [hasSignature, setHasSignature] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Get current location on mount
  useEffect(() => {
    getLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get location
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalizacion no soportada');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setLocationLoading(false);
      },
      (err) => {
        console.error('[SignatureCapture] Location error:', err);
        setLocationError('No se pudo obtener la ubicacion');
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, []);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle signature change
  const handleSignatureChange = (hasSig) => {
    setHasSignature(hasSig);
  };

  // Clear signature
  const handleClearSignature = () => {
    canvasRef.current?.clear();
    setHasSignature(false);
  };

  // Validate form
  const validateForm = () => {
    if (!formData.signer_name.trim()) {
      setError('Por favor ingrese el nombre del firmante');
      return false;
    }
    if (!hasSignature) {
      setError('Por favor dibuje la firma');
      return false;
    }
    return true;
  };

  // Handle preview
  const handlePreview = () => {
    if (!validateForm()) return;

    const signatureDataUrl = canvasRef.current?.toDataURL();
    setPreviewData({
      ...formData,
      signature_type: signatureType,
      signature_url: signatureDataUrl,
      location,
      captured_at: new Date().toISOString(),
      captured_by: user?.full_name || user?.email,
    });
    setShowPreview(true);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      const signatureDataUrl = canvasRef.current?.toDataURL();

      const { error: captureError } = await captureSignature({
        project_id: projectId,
        task_id: taskId || null,
        signature_type: signatureType,
        signer_name: formData.signer_name.trim(),
        signer_role: formData.signer_role.trim() || null,
        signer_id_number: formData.signer_id_number.trim() || null,
        signature_data: signatureDataUrl,
        location,
        notes: formData.notes.trim() || null,
      });

      if (captureError) throw captureError;

      // Success - navigate back
      navigate(-1, {
        state: {
          success: true,
          message: 'Firma capturada exitosamente',
        },
      });
    } catch (err) {
      console.error('[SignatureCapture] Submit error:', err);
      setError(err.message || 'Error al guardar la firma');
      setShowPreview(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Get signature type label
  const getTypeLabel = (type) => {
    return SIGNATURE_TYPE_LABELS[type] || type;
  };

  // Format location
  const formatLocation = (loc) => {
    if (!loc) return 'No disponible';
    return `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`;
  };

  if (!typeAllowed) {
    return (
      <div className="signature-capture">
        <div className="signature-capture-header">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Volver"
          >
            <FaArrowLeft />
          </button>
          <div className="header-info">
            <h1 className="page-title">Captura de Firma</h1>
            <span className="signature-type-label">
              {getTypeLabel(signatureType)}
            </span>
          </div>
        </div>

        <div className="error-message">
          <FaExclamationCircle />
          <span>{signatureDenialMessage(signatureType)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="signature-capture">
      {/* Header */}
      <div className="signature-capture-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Volver"
        >
          <FaArrowLeft />
        </button>
        <div className="header-info">
          <h1 className="page-title">Captura de Firma</h1>
          <span className="signature-type-label">
            {getTypeLabel(signatureType)}
          </span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="error-message">
          <FaExclamationCircle />
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Descartar el error">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Form */}
      <div className="signature-form">
        {/* Signer information */}
        <div className="form-section">
          <h2 className="section-title">
            <FaUser /> Informacion del Firmante
          </h2>

          <div className="form-group">
            <label htmlFor="signer_name">
              Nombre completo <span className="required">*</span>
            </label>
            <input
              type="text"
              id="signer_name"
              name="signer_name"
              value={formData.signer_name}
              onChange={handleInputChange}
              placeholder="Nombre del firmante"
              autoComplete="name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="signer_role">
              <FaBriefcase /> Cargo / Rol
            </label>
            <input
              type="text"
              id="signer_role"
              name="signer_role"
              value={formData.signer_role}
              onChange={handleInputChange}
              placeholder="Ej: Gerente de Mantenimiento"
              autoComplete="organization-title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="signer_id_number">
              <FaIdCard /> Numero de Identificacion
            </label>
            <input
              type="text"
              id="signer_id_number"
              name="signer_id_number"
              value={formData.signer_id_number}
              onChange={handleInputChange}
              placeholder="Cedula o ID (opcional)"
            />
          </div>
        </div>

        {/* Signature canvas */}
        <div className="form-section">
          <h2 className="section-title">
            <FaPen /> Firma Digital
          </h2>

          <div className="signature-canvas-wrapper">
            <SignatureCanvas
              ref={canvasRef}
              height={200}
              lineWidth={2.5}
              onChange={handleSignatureChange}
              disabled={submitting}
            />
          </div>

          <div className="canvas-actions">
            <button
              type="button"
              className="clear-btn"
              onClick={handleClearSignature}
              disabled={!hasSignature || submitting}
            >
              <FaEraser /> Limpiar
            </button>
          </div>
        </div>

        {/* Location info */}
        <div className="form-section">
          <h2 className="section-title">
            <FaMapMarkerAlt /> Ubicacion
          </h2>

          <div className="location-info">
            {locationLoading ? (
              <div className="location-loading">
                <FaSpinner className="spinner" />
                <span>Obteniendo ubicacion...</span>
              </div>
            ) : locationError ? (
              <div className="location-error">
                <FaExclamationCircle />
                <span>{locationError}</span>
                <button type="button" onClick={getLocation}>
                  Reintentar
                </button>
              </div>
            ) : location ? (
              <div className="location-success">
                <FaMapMarkerAlt />
                <span>{formatLocation(location)}</span>
                {location.accuracy && (
                  <span className="accuracy">
                    (precision: {Math.round(location.accuracy)}m)
                  </span>
                )}
              </div>
            ) : (
              <div className="location-pending">
                <span>Ubicacion no disponible</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h2 className="section-title">Notas (opcional)</h2>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Agregar notas adicionales..."
            rows={3}
            disabled={submitting}
          />
        </div>

        {/* Submit button */}
        <div className="submit-section">
          <button
            type="button"
            className="preview-btn"
            onClick={handlePreview}
            disabled={submitting || !formData.signer_name.trim() || !hasSignature}
          >
            <FaCheck /> Revisar y Confirmar
          </button>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && previewData && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Firma</h2>
              <button
                className="modal-close"
                onClick={() => setShowPreview(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Signature preview */}
              <div className="preview-signature">
                <img
                  src={previewData.signature_url}
                  alt="Firma"
                  className="signature-preview-img"
                />
              </div>

              {/* Info summary */}
              <div className="preview-info">
                <div className="preview-row">
                  <span className="preview-label">Tipo:</span>
                  <span className="preview-value">
                    {getTypeLabel(previewData.signature_type)}
                  </span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Nombre:</span>
                  <span className="preview-value">{previewData.signer_name}</span>
                </div>
                {previewData.signer_role && (
                  <div className="preview-row">
                    <span className="preview-label">Cargo:</span>
                    <span className="preview-value">{previewData.signer_role}</span>
                  </div>
                )}
                {previewData.signer_id_number && (
                  <div className="preview-row">
                    <span className="preview-label">ID:</span>
                    <span className="preview-value">{previewData.signer_id_number}</span>
                  </div>
                )}
                <div className="preview-row">
                  <span className="preview-label">Ubicacion:</span>
                  <span className="preview-value">
                    {formatLocation(previewData.location)}
                  </span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Fecha:</span>
                  <span className="preview-value">
                    {new Date(previewData.captured_at).toLocaleString('es-CR')}
                  </span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Capturada por:</span>
                  <span className="preview-value">{previewData.captured_by}</span>
                </div>
              </div>

              {previewData.notes && (
                <div className="preview-notes">
                  <span className="preview-label">Notas:</span>
                  <p>{previewData.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowPreview(false)}
                disabled={submitting}
              >
                Editar
              </button>
              <button
                className="confirm-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="spinner" /> Guardando...
                  </>
                ) : (
                  <>
                    <FaCheck /> Confirmar Firma
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCapture;
